import { getLegalValues } from "@lib//api/getLegalValues";
import {
    getAccessScopeId,
    keepPreviousDataForSameAccessScope,
    queryAccess,
} from "@lib/access/access";
import {
    getAttendances,
    GetAttendancesQuery,
    getAttendancesV2,
    getCompanyInfo,
    getCompanyLegalInfo,
    getCompanyPaymentInfo,
    getCompanySiders,
    getCompanyVmsConfiguration,
    getCustomJobTitles,
    getFeaturesPolicy,
    getFullFeedback,
    getHolidays,
    getInvoices,
    GetInvoicesParams,
    GetInvoicesResponse,
    getJobDescriptionJobTitles,
    getJobDescriptionOptions,
    getJobDescriptions,
    getJobDescriptionSubtasks,
    getJobTypes,
    getNumberOfAttendances,
    getNumberOfContractsBatches,
    getOrganisationCompletionStatus,
    getOrganisationUsers,
    getQuotation,
    getSider,
    getSiders,
    getTask,
    getUserInfoQueryOptions,
    searchJobTitles,
} from "@lib/api";
import { getCompanyDepartments } from "@lib/api/getCompanyDepartments";
import { getCompanyMotives } from "@lib/api/getCompanyMotives";
import { getFlexiblePlanning } from "@lib/api/getFlexiblePlanning";
import {
    getFlexiblePlanningQuotation,
    GetFlexiblePlanningQuotationParams,
    GetFlexiblePlanningQuotationQuery,
} from "@lib/api/getFlexiblePlanningQuotation";
import { getGuestTask } from "@lib/api/getGuestTask";
import { getOrganisationDocuments } from "@lib/api/getOrganisationDocuments";
import { getRemunerationPolicy } from "@lib/api/getRemunerationPolicy";
import { getShiftRecurrences } from "@lib/api/getShiftRecurrences";
import { getSidersProspects } from "@lib/api/getSidersPropects";
import { getTaskPreSelection } from "@lib/api/getTaskPreselection";
import { getTasksV2 } from "@lib/api/getTasksV2";
import { getDateRange } from "@lib/attendance/filters/dateRange";
import {
    type TimesheetsDateModeUnion,
    type TimesheetsSearchModeUnion,
} from "@lib/attendance/searchEnums";
import { queryOptions } from "@tanstack/react-query";

import { normalizeDocuments } from "./normalizeDocuments";
import { normalizeJobDescriptions } from "./normalizeJobDescriptions";

const oneDay = 1000 * 60 * 60 * 24;
const fiveMinutes = 1000 * 60 * 5;
export const user = {
    key: ["user"],
    detail: getUserInfoQueryOptions,
};

export const legalValues = {
    key: ["legalValues"],
    list: () => ({
        queryKey: [...legalValues.key, "list"],
        queryFn: getLegalValues,
        staleTime: oneDay,
    }),
};

export const ATTENDANCE_LIMIT = 50;
export const attendance = {
    key: ["attendance"],
    list: ({
        page = 1,
        dateMode,
        day,
        week,
        month,
        searchMode,
        siderId,
        managerId,
        ...args
    }: Omit<GetAttendancesQuery, "limit" | "offset" | "startDate" | "endDate"> & {
        page: number;
        dateMode?: TimesheetsDateModeUnion;
        day?: Date;
        week?: string;
        month?: string;
        searchMode?: TimesheetsSearchModeUnion;
    }) => {
        const access = queryAccess("account", [
            ...attendance.key,
            "list",
            page,
            dateMode,
            day,
            week,
            month,
            siderId,
            managerId,
            args,
        ]);
        return {
            ...access,
            queryFn: ({ signal }) => {
                const offset = (page - 1) * ATTENDANCE_LIMIT;
                // compute startDate and endDate based on mode and day, week, month
                const { startDate, endDate } = getDateRange({
                    dateMode,
                    day,
                    week,
                    month,
                });
                return getAttendances(
                    {
                        limit: ATTENDANCE_LIMIT,
                        offset,
                        startDate,
                        endDate,
                        ...(searchMode === "sider" ? { siderId } : { managerId }),
                        ...args,
                    },
                    signal,
                );
            },
            placeholderData: (previousData, previousQuery) =>
                keepPreviousDataForSameAccessScope(
                    previousData,
                    previousQuery?.queryKey,
                    access.queryKey,
                ),
        };
    },
    toValidateCount: () => {
        const access = queryAccess("account", [...attendance.key, "toValidateCount"]);
        return {
            ...access,
            queryFn: getNumberOfAttendances,
        };
    },
};

/** Shares base key with {@link attendance} so one `invalidateQueries({ queryKey: attendance.key })` refreshes v1 lists, v2 lists, and related attendance queries. */
export const attendanceV2 = {
    key: attendance.key,
    list: (query: Parameters<typeof getAttendancesV2>[0]["query"]) => {
        const access = queryAccess("accountOrNetwork", [...attendance.key, "v2", "list", query]);
        return queryOptions({
            ...access,
            queryFn: ({ signal }) => getAttendancesV2({ query, signal }),
        });
    },
};

export const company = {
    key: ["company"],
    detail: () => {
        const access = queryAccess("accountOrNetwork", [...company.key, "detail"]);
        return {
            ...access,
            queryFn: () => getCompanyInfo(),
        };
    },
    companySiders: (search: string) => {
        const access = queryAccess("accountOrNetwork", [...company.key, "companySiders", search]);
        return {
            ...access,
            queryFn: ({ signal }) =>
                getCompanySiders({ query: { search, offset: 0, limit: 20 } }, signal),
            enabled: access.enabled && !!search,
        };
    },
    companyMotives: () => {
        const access = queryAccess("account", [...company.key, "companyMotives"]);
        return {
            ...access,
            queryFn: ({ signal }) => getCompanyMotives(signal),
        };
    },
    completionStatus: () => {
        const access = queryAccess("account", [...company.key, "completionStatus"]);
        return {
            ...access,
            queryFn: getOrganisationCompletionStatus,
            staleTime: fiveMinutes,
        };
    },
    paymentInfo: (organisationId: string) => {
        const access = queryAccess("account", [...company.key, "paymentInfo", organisationId]);
        return {
            ...access,
            queryFn: () => getCompanyPaymentInfo({ organisationId }),
            enabled: access.enabled && !!organisationId,
        };
    },
    legalInfo: (organisationId: string) => {
        const access = queryAccess("account", [...company.key, "legalInfo", organisationId]);
        return {
            ...access,
            queryFn: () => getCompanyLegalInfo(organisationId),
            enabled: access.enabled && !!organisationId,
        };
    },
    departments: (
        params: Parameters<typeof getCompanyDepartments>[0],
        query: Parameters<typeof getCompanyDepartments>[1],
    ) => {
        const access = queryAccess("account", [...company.key, "departments", params, query]);
        return {
            ...access,
            queryFn: () => getCompanyDepartments(params, query),
        };
    },
    vmsConfiguration: (params: { organisationId: string }) => {
        const access = queryAccess("account", [
            ...company.key,
            "vmsConfiguration",
            params.organisationId,
        ]);
        return {
            ...access,
            queryFn: () => getCompanyVmsConfiguration(params),
            enabled: access.enabled && !!params.organisationId,
        };
    },
    featuresPolicy: () => {
        const access = queryAccess("accountOrNetwork", [...company.key, "featuresPolicy"]);
        return {
            ...access,
            queryFn: getFeaturesPolicy,
        };
    },
    remunerationPolicy: () => {
        const accountOrNetworkId = getAccessScopeId("accountOrNetwork");
        const access = queryAccess("accountOrNetwork", [...company.key, "remunerationPolicy"]);
        return {
            ...access,
            queryFn: () => getRemunerationPolicy({ organisationId: accountOrNetworkId! }),
        };
    },
    users: (query: Parameters<typeof getOrganisationUsers>[0]) => {
        const access = queryAccess("accountOrNetwork", [...company.key, "users", query]);
        return {
            ...access,
            queryFn: ({ signal }) => getOrganisationUsers(query, signal),
        };
    },
};

export const feedback = {
    key: ["feedback"],
    detail: (id: string) => {
        const access = queryAccess("account", [...feedback.key, "detail", id]);
        return {
            ...access,
            queryFn: () => getFullFeedback({ feedbackId: id }),
            enabled: access.enabled && !!id,
        };
    },
};

export const task = {
    key: ["task"],
    detail: (id: string) => {
        const access = queryAccess("account", [...task.key, "detail", id]);
        return {
            ...access,
            queryFn: () => getTask(id),
            enabled: access.enabled && !!id,
        };
    },
    planning: (taskId: string) => {
        const access = queryAccess("account", [...task.key, "planning", taskId]);
        return {
            ...access,
            queryFn: () => getFlexiblePlanning(taskId),
        };
    },
    flexiblePlanningQuotation: ({
        params,
        query,
    }: {
        params: GetFlexiblePlanningQuotationParams;
        query: GetFlexiblePlanningQuotationQuery;
    }) => {
        const access = queryAccess("account", [
            ...task.key,
            "flexiblePlanningQuotation",
            params.id,
            query,
        ]);
        return {
            ...access,
            queryFn: ({ signal }: { signal: AbortSignal }) =>
                getFlexiblePlanningQuotation({ params, query }, signal),
        };
    },
};

export const shiftRecurrences = {
    key: ["shiftRecurrences"],
    detail: (shiftId: string) => {
        const access = queryAccess("account", [...shiftRecurrences.key, "list", shiftId]);
        return {
            ...access,
            queryFn: ({ signal }) => getShiftRecurrences(shiftId, signal),
            enabled: access.enabled && !!shiftId,
        };
    },
};
export const taskPreSelection = {
    key: ["preSelection"],
    list: (taskId: string) => {
        const access = queryAccess("account", [...taskPreSelection.key, "list", taskId]);
        return {
            ...access,
            queryFn: ({ signal }) => getTaskPreSelection(taskId, signal),
            enabled: access.enabled && !!taskId,
        };
    },
};

export const holiday = {
    key: ["holiday"],
    list: (year: number) => {
        const access = queryAccess("account", [...holiday.key, "list", year]);
        return queryOptions({
            ...access,
            queryFn: () => getHolidays(year),
            enabled: access.enabled && !!year,
        });
    },
    holidaysInPeriod: (years: number[]) => ({
        queries: years.map((year) => holiday.list(year)),
        combine: (results) => {
            const concatenated = results.reduce(
                (acc, result) => [...acc, ...(result.data ?? [])],
                [] as Awaited<ReturnType<typeof getHolidays>>,
            );
            const seenHolidayKeys = new Set<string>();
            const deduped = concatenated.filter((holiday) => {
                const key = `${holiday.name}-${holiday.startDate.toISOString()}-${holiday.endDate.toISOString()}`;
                if (seenHolidayKeys.has(key)) return false;
                seenHolidayKeys.add(key);
                return true;
            });

            return {
                holidays: deduped,
                isLoading: results.some((result) => result.isPending),
            };
        },
    }),
};

export const sider = {
    key: ["sider"],
    detail: (siderId: string, query: Parameters<typeof getSider>[1]) => {
        const access = queryAccess("account", [...sider.key, "detail", siderId, query]);
        return {
            ...access,
            queryFn: () => getSider({ siderId }, query),
            enabled: access.enabled && !!siderId,
        };
    },
    list: (params: Parameters<typeof getSiders>[0]) => {
        const access = queryAccess("account", [...sider.key, "list", { ...params }]);
        return {
            ...access,
            queryFn: async ({ signal }) => getSiders(params, signal),
            enabled: access.enabled && !!params.taskId,
            select: (data: Awaited<ReturnType<typeof getSiders>>) =>
                [...(data?.siders ?? [])].sort((a, b) => {
                    const aIsRecommended = a.recommendationReason === "manual_recommendation";
                    const bIsRecommended = b.recommendationReason === "manual_recommendation";

                    if (aIsRecommended === bIsRecommended) return 0;
                    return aIsRecommended ? -1 : 1;
                }),
            placeholderData: (previousData, previousQuery) =>
                keepPreviousDataForSameAccessScope(
                    previousData,
                    previousQuery?.queryKey,
                    access.queryKey,
                ),
        };
    },
    prospectList: (params: Parameters<typeof getSidersProspects>[0]) => {
        const access = queryAccess("account", [...sider.key, "prospectList", { ...params }]);
        return {
            ...access,
            queryFn: () => getSidersProspects(params),
            enabled: access.enabled && !!params.taskId,
            select: (data: Awaited<ReturnType<typeof getSidersProspects>>) => data?.siders ?? [],
            placeholderData: (previousData, previousQuery) =>
                keepPreviousDataForSameAccessScope(
                    previousData,
                    previousQuery?.queryKey,
                    access.queryKey,
                ),
        };
    },
};

export const quotation = {
    key: ["quotation"],
    detail: (taskId: string) => {
        const access = queryAccess("account", [...quotation.key, "detail", taskId]);
        return {
            ...access,
            queryFn: () => getQuotation({ taskId }),
            enabled: access.enabled && !!taskId,
        };
    },
};

export const jobDescriptions = {
    key: ["jobDescriptions"],
    list: () => {
        const access = queryAccess("accountOrNetwork", [...jobDescriptions.key, "list"]);
        return {
            ...access,
            queryFn: getJobDescriptions,
            select: (data) => normalizeJobDescriptions(data),
            staleTime: fiveMinutes,
        };
    },
};

export const jobTitles = {
    key: ["jobTitles"],
    list: () => {
        const access = queryAccess("accountOrNetwork", [...jobTitles.key, "list"]);
        return {
            ...access,
            queryFn: getJobDescriptionJobTitles,
            staleTime: oneDay,
        };
    },
    search: (query: string) => ({
        ...queryAccess("accountOrNetwork", [...jobTitles.key, "search", query]),
        queryFn: () => (query ? searchJobTitles(query) : []),
    }),
};

export const customJobTitles = {
    key: ["customJobTitles"],
    list: () => {
        const access = queryAccess("accountOrNetwork", [...customJobTitles.key, "list"]);
        return {
            ...access,
            queryFn: getCustomJobTitles,
        };
    },
};

export const jobDescriptionJobTitle = {
    key: ["jobDescriptionJobTitle"],
    list: () => {
        const access = queryAccess("accountOrNetwork", [...jobDescriptionJobTitle.key, "list"]);
        return {
            ...access,
            queryFn: () => getJobDescriptionJobTitles(),
            select: (data: Awaited<ReturnType<typeof getJobDescriptionJobTitles>>) => {
                return data.map((jobTitle) => ({
                    value: jobTitle.id,
                    label: jobTitle.name,
                }));
            },
        };
    },
};

export const jobDescriptionSubtasks = {
    key: ["jobDescriptionSubtasks"],
    list: (jobTitleId: string) => {
        const access = queryAccess("accountOrNetwork", [
            ...jobDescriptionSubtasks.key,
            "list",
            jobTitleId,
        ]);
        return {
            ...access,
            queryFn: () => {
                if (!jobTitleId) return Promise.resolve([]);
                return getJobDescriptionSubtasks(jobTitleId);
            },
            enabled: access.enabled && !!jobTitleId,
        };
    },
};

export const jobDescriptionOptions = {
    key: ["jobDescriptionOptions"],
    list: () => {
        const access = queryAccess("accountOrNetwork", [...jobDescriptionOptions.key, "list"]);
        return {
            ...access,
            queryFn: getJobDescriptionOptions,
            staleTime: oneDay,
        };
    },
};

export const jobTypes = {
    key: ["jobTypes"],
    list: () => {
        const access = queryAccess("account", [...jobTypes.key, "list"]);
        return {
            ...access,
            queryFn: getJobTypes,
        };
    },
};

let autocompleteService: google.maps.places.AutocompleteService | null = null;

export const googlePlacesPredictions = {
    key: ["googlePlacesPredictions"],
    list: (searchValue: string, locale?: string) => ({
        queryKey: [...googlePlacesPredictions.key, "list", searchValue],
        queryFn: async (): Promise<google.maps.places.AutocompletePrediction[]> => {
            // Ensure the service is initialized once
            if (!autocompleteService) {
                autocompleteService = new window.google.maps.places.AutocompleteService();
            }

            const query = {
                componentRestrictions: {
                    country: [locale ?? "fr"],
                },
                input: searchValue,
            };

            return new Promise((resolve, reject) => {
                autocompleteService?.getPlacePredictions(query, (predictions, status) => {
                    if (
                        status === window.google.maps.places.PlacesServiceStatus.OK &&
                        predictions
                    ) {
                        resolve(predictions);
                    } else {
                        reject(status);
                    }
                });
            });
        },
    }),
};

export const invoice = {
    key: ["invoice"],
    list: (args: GetInvoicesParams[]) => ({
        queries: args.map(({ startDate, endDate }) => {
            const access = queryAccess("account", [...invoice.key, "list", startDate, endDate]);
            return queryOptions({
                ...access,
                queryFn: () => getInvoices({ startDate, endDate }),
            });
        }),
        combine: (results) => {
            const concatenated = results.reduce(
                (acc, result) => [...acc, ...(result.data?.invoices ?? [])],
                [] as GetInvoicesResponse["invoices"],
            );
            const seenIds = new Set<string>();
            const deduped = concatenated.filter((inv) => {
                if (seenIds.has(inv.id)) return false;
                seenIds.add(inv.id);
                return true;
            });
            return {
                invoices: deduped.sort(
                    (a, b) =>
                        new Date(b.weekStartDate).getTime() - new Date(a.weekStartDate).getTime(),
                ),
                isLoading: results.some((result) => result.isPending),
            };
        },
    }),
};

export const tasksV2 = {
    key: ["tasksV2"],
    list: (query: Parameters<typeof getTasksV2>[0]["query"]) => {
        const access = queryAccess("accountOrNetwork", [...tasksV2.key, "list", query]);
        return queryOptions({
            ...access,
            queryFn: ({ signal }) => getTasksV2({ query, signal }),
        });
    },
};

export const documents = {
    key: ["documents"],
    list: (query: Parameters<typeof getOrganisationDocuments>[0]["query"]) => {
        const access = queryAccess("accountOrNetwork", [...documents.key, "list", query]);
        return queryOptions({
            ...access,
            queryFn: ({ signal }) => getOrganisationDocuments({ query, signal }),
            select: normalizeDocuments,
        });
    },
    unsignedCount: () => {
        const access = queryAccess("accountOrNetwork", [...documents.key, "unsignedCount"]);
        return queryOptions({
            ...access,
            queryFn: getNumberOfContractsBatches,
            select: (data) => data.count,
        });
    },
};

export const guestTask = {
    key: ["guestTask"],
    detail: (taskId: string, token: string) =>
        queryOptions({
            queryKey: [...guestTask.key, "detail", taskId, token],
            queryFn: () => getGuestTask({ taskId, token }),
            enabled: !!taskId,
        }),
};
